import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { uploadExcelJob, getJobs, getJob, deleteJob, getJobRecords, removeDuplicates, deleteByPrefix, calculateDuplicates, getOperations, deduplicateOperation, deletePrefixOperation, combineOperation, getExcelDownloadUrl } from './services/excelApi';

export default function ExcelManagementView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Multi-select & Operations State
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [activeOperation, setActiveOperation] = useState(null);

  // Data Viewer State
  const [viewingJob, setViewingJob] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({});
  const [dupColumns, setDupColumns] = useState([]);
  
  // Delete by Prefix State
  const [prefixCol, setPrefixCol] = useState('');
  const [prefixString, setPrefixString] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Global Delete by Prefix State
  const [globalPrefixCol, setGlobalPrefixCol] = useState('');
  const [globalPrefixString, setGlobalPrefixString] = useState('');
  const [showGlobalPrefixConfirm, setShowGlobalPrefixConfirm] = useState(false);
  const [globalPrefixScope, setGlobalPrefixScope] = useState('selected'); // 'selected' or 'all'

  // Global Deduplicate State
  const [globalDupCols, setGlobalDupCols] = useState([]);
  const [showGlobalDupConfirm, setShowGlobalDupConfirm] = useState(false);

  useEffect(() => {
    loadJobs();
    loadOperations();
    
    // Polling for updates if any job or operation is processing
    const interval = setInterval(() => {
      setJobs(currentJobs => {
        const needsPolling = currentJobs.some(j => !['ready', 'error'].includes(j.status));
        if (needsPolling) {
          loadJobs(false);
        }
        return currentJobs;
      });
      setOperations(currentOps => {
        const needsPolling = currentOps.some(op => !['completed', 'error'].includes(op.status));
        if (needsPolling) {
          loadOperations(false);
        }
        return currentOps;
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  async function loadOperations(showLoading = false) {
    try {
      const ops = await getOperations();
      setOperations(ops || []);
      const active = ops.find(op => !['completed', 'error'].includes(op.status));
      setActiveOperation(active || null);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadJobs(showLoading = true) {
    if (showLoading) setLoading(true);
    try {
      const data = await getJobs();
      setJobs(data || []);
      
      // Update viewing job if it exists to get fresh stats
      setViewingJob(prev => {
        if (prev) {
          const updated = data.find(j => j.id === prev.id);
          return updated ? updated : prev;
        }
        return prev;
      });
    } catch (e) {
      if (showLoading) toast.error('Failed to load excel jobs');
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadExcelJob(file, (percent) => setUploadProgress(percent));
      toast.success('File uploaded and processing started');
      setFile(null);
      setUploadProgress(0);
      document.getElementById('excel-upload').value = '';
      loadJobs(false);
    } catch (error) {
      toast.error(error.message || 'Upload failed');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this excel dataset?')) return;
    try {
      await deleteJob(id);
      toast.success('Deleted successfully');
      if (viewingJob?.id === id) setViewingJob(null);
      loadJobs(false);
    } catch (e) {
      toast.error('Failed to delete');
    }
  }

  async function loadRecords(jobId, currentPage = 1, currentFilters = filters) {
    setRecordsLoading(true);
    try {
      const params = { page: currentPage, limit: 20 };
      Object.keys(currentFilters).forEach(k => {
        if (currentFilters[k]) params[`filter_${k}`] = currentFilters[k];
      });
      const data = await getJobRecords(jobId, params);
      setRecords(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setRecordsLoading(false);
    }
  }

  function handleViewData(job) {
    setViewingJob(job);
    setPage(1);
    setFilters({});
    setDupColumns([]);
    loadRecords(job.id, 1, {});
  }

  async function handleRemoveDuplicates() {
    if (dupColumns.length === 0) {
      toast.error('Please select at least one column to check for duplicates');
      return;
    }
    if (viewingJob.status === 'calculating') {
      toast.error('Please wait for the duplicate calculation to finish...');
      return;
    }
    setShowConfirm(true);
  }

  async function confirmRemoveDuplicates() {
    setShowConfirm(false);
    try {
      await removeDuplicates(viewingJob.id, dupColumns);
      toast.success('Duplicate removal started');
      loadJobs(false);
    } catch (e) {
      toast.error(e.message || 'Failed to remove duplicates');
    }
  }

  async function handleDupColumnChange(newColumns) {
    setDupColumns(newColumns);
    if (!viewingJob) return;
    
    try {
      await calculateDuplicates(viewingJob.id, newColumns);
      toast.success('Calculating duplicates...');
      loadJobs(false);
    } catch (e) {
      toast.error('Failed to start duplicate calculation');
    }
  }

  async function handleDeleteByPrefix() {
    if (!prefixCol) {
      toast.error('Please select a column for prefix deletion');
      return;
    }
    if (!prefixString.trim()) {
      toast.error('Please enter the prefixes to delete (e.g. 11-19, 1, 2)');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete all records where ${prefixCol} starts with: ${prefixString}?`)) return;

    try {
      await deleteByPrefix(viewingJob.id, prefixCol, prefixString);
      toast.success('Prefix deletion started in background');
      setPrefixString('');
      loadJobs(false);
    } catch (e) {
      toast.error(e.message || 'Failed to start deletion');
    }
  }

  async function handleGlobalDeduplicate() {
    if (globalDupCols.length === 0) {
      toast.error('Please select at least one column');
      return;
    }
    setShowGlobalDupConfirm(false);
    try {
      await deduplicateOperation(selectedJobs, globalDupCols, 'first');
      toast.success('Global deduplication started');
      setSelectedJobs([]);
      setGlobalDupCols([]);
      loadOperations();
    } catch (e) {
      toast.error(e.message || 'Failed to start deduplication');
    }
  }

  async function handleGlobalDeletePrefix() {
    if (!globalPrefixCol || !globalPrefixString) {
      toast.error('Column and prefixes are required');
      return;
    }
    setShowGlobalPrefixConfirm(false);
    try {
      const targets = globalPrefixScope === 'all' ? jobs.map(j => j.id) : selectedJobs;
      await deletePrefixOperation(targets, globalPrefixCol, globalPrefixString);
      toast.success('Prefix deletion started');
      setGlobalPrefixCol('');
      setGlobalPrefixString('');
      setSelectedJobs([]);
      loadOperations();
    } catch (e) {
      toast.error(e.message || 'Failed to start prefix deletion');
    }
  }

  async function handleCombineFiles() {
    if (selectedJobs.length < 2) {
      toast.error('Select at least 2 files to combine');
      return;
    }
    try {
      await combineOperation(selectedJobs, false, []);
      toast.success('Combine started');
      setSelectedJobs([]);
      loadOperations();
    } catch (e) {
      toast.error(e.message || 'Failed to combine files');
    }
  }

  function toggleJobSelection(id) {
    if (selectedJobs.includes(id)) {
      setSelectedJobs(selectedJobs.filter(j => j !== id));
    } else {
      setSelectedJobs([...selectedJobs, id]);
    }
  }

  if (viewingJob) {
    return (
      <div className="panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div className="panel__header">
          <div className="panel__header--split">
            <div>
              <button className="button button--ghost" onClick={() => setViewingJob(null)} style={{ padding: 0, marginBottom: '8px' }}>← Back to Datasets</button>
              <h2>{viewingJob.originalName}</h2>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}><strong>Total:</strong> {(viewingJob.totalRows || 0).toLocaleString()}</span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    <strong>Unique:</strong> {viewingJob.status === 'calculating' ? 'Calculating...' : (viewingJob.uniqueRows || viewingJob.totalRows || 0).toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                    <strong>Duplicates:</strong> {viewingJob.status === 'calculating' ? 'Calculating...' : (viewingJob.duplicateRows || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Remove Duplicates by:</span>
                <select 
                  className="form-input" 
                  value=""
                  onChange={(e) => {
                    if (!dupColumns.includes(e.target.value) && e.target.value) {
                      handleDupColumnChange([...dupColumns, e.target.value]);
                    }
                  }}
                  style={{ width: '150px' }}
                >
                  <option value="">Add column...</option>
                  {viewingJob.columns?.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button 
                  className="button button--secondary" 
                  onClick={handleRemoveDuplicates}
                  disabled={viewingJob.status === 'calculating'}
                >
                  {viewingJob.status === 'calculating' ? 'Calculating...' : 'Remove'}
                </button>
              </div>
              {dupColumns.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {dupColumns.map(c => (
                    <span key={c} style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {c} <span style={{ cursor: 'pointer', color: '#ef4444' }} onClick={() => handleDupColumnChange(dupColumns.filter(col => col !== c))}>×</span>
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Delete if Starts With:</span>
                <select 
                  className="form-input" 
                  value={prefixCol}
                  onChange={(e) => setPrefixCol(e.target.value)}
                  style={{ width: '120px' }}
                >
                  <option value="">Col...</option>
                  {viewingJob.columns?.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="11-19, 1, 2" 
                  value={prefixString}
                  onChange={(e) => setPrefixString(e.target.value)}
                  style={{ width: '180px' }}
                />
                <button className="button button--secondary" style={{ background: '#fef2f2', color: '#ef4444', borderColor: '#fca5a5' }} onClick={handleDeleteByPrefix}>Delete</button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <table className="data-table" style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#475569', fontSize: '0.85rem' }}>#</th>
                {viewingJob.columns?.slice(0, 15).map(col => (
                  <th key={col} style={{ padding: '12px', textAlign: 'left', color: '#475569', fontSize: '0.85rem', minWidth: '150px' }}>
                    <div style={{ marginBottom: '8px' }}>{col}</div>
                    <input 
                      type="text" 
                      placeholder="Filter..." 
                      className="form-input" 
                      style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                      value={filters[col] || ''}
                      onChange={(e) => {
                        const newFilters = { ...filters, [col]: e.target.value };
                        setFilters(newFilters);
                        setPage(1);
                        loadRecords(viewingJob.id, 1, newFilters);
                      }}
                    />
                  </th>
                ))}
                {viewingJob.columns?.length > 15 && <th style={{ padding: '12px' }}>...</th>}
              </tr>
            </thead>
            <tbody>
              {recordsLoading ? (
                <tr><td colSpan={viewingJob.columns?.length + 1} style={{ padding: '24px', textAlign: 'center' }}>Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={viewingJob.columns?.length + 1} style={{ padding: '24px', textAlign: 'center' }}>No records found.</td></tr>
              ) : (
                records.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px', fontSize: '0.85rem', color: '#64748b' }}>{(page - 1) * 20 + i + 1}</td>
                    {viewingJob.columns?.slice(0, 15).map(col => (
                      <td key={col} style={{ padding: '12px', fontSize: '0.85rem' }}>{row[col]}</td>
                    ))}
                    {viewingJob.columns?.length > 15 && <td style={{ padding: '12px' }}>...</td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Showing page {page} of {Math.ceil(total / 20) || 1} ({total.toLocaleString()} total filtered)</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="button button--secondary" disabled={page === 1} onClick={() => { setPage(page - 1); loadRecords(viewingJob.id, page - 1); }}>Previous</button>
            <button className="button button--secondary" disabled={page >= Math.ceil(total / 20)} onClick={() => { setPage(page + 1); loadRecords(viewingJob.id, page + 1); }}>Next</button>
          </div>
        </div>
        
        {showConfirm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#0f172a' }}>Remove Duplicates?</h3>
              <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '24px' }}>Are you sure you want to remove duplicate records based on the selected columns?</p>
              
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span style={{ color: '#64748b' }}>Total Records:</span> 
                  <strong style={{ color: '#334155' }}>{(viewingJob.totalRows || 0).toLocaleString()}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span style={{ color: '#64748b' }}>Unique Records:</span> 
                  <strong style={{ color: '#334155' }}>{(viewingJob.uniqueRows || viewingJob.totalRows || 0).toLocaleString()}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span style={{ color: '#64748b' }}>Duplicate Records:</span> 
                  <strong style={{ color: '#ef4444' }}>{(viewingJob.duplicateRows || 0).toLocaleString()}</strong>
                </div>
              </div>
              
              <p style={{ fontSize: '0.9rem', color: '#64748b', textAlign: 'center', marginBottom: '24px' }}>
                These <strong>{(viewingJob.duplicateRows || 0).toLocaleString()}</strong> duplicate records will be permanently removed.
              </p>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button className="button button--secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
                <button className="button button--primary" style={{ background: '#ef4444', borderColor: '#ef4444' }} onClick={confirmRemoveDuplicates}>Remove Duplicates</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div className="panel__header">
        <div className="panel__header--split">
          <div>
            <span className="panel__eyebrow">Data Hub</span>
            <h2>Excel Management</h2>
            <p>Upload, process, filter, and manage very large datasets (up to 10M records).</p>
          </div>
        </div>
      </div>

      <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <form onSubmit={handleUpload} style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0, flex: 1, minWidth: '200px' }}>
            <label className="form-label" htmlFor="excel-upload">Upload Excel / CSV File</label>
            <input 
              id="excel-upload"
              type="file" 
              className="form-input" 
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>
          <button type="submit" className="button button--primary" disabled={uploading || !file}>
            {uploading ? `Uploading ${uploadProgress}%` : 'Upload Data'}
          </button>
        </form>
        {uploading && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#10b981', width: `${uploadProgress}%`, transition: 'width 0.2s' }}></div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="empty-state">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">No datasets available. Upload one to get started.</div>
      ) : (
        <>
          <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#0f172a', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>DATA SUMMARY</h3>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Files</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#334155' }}>{jobs.length}</div>
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Records</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#334155' }}>{jobs.reduce((sum, j) => sum + (j.totalRows || 0), 0).toLocaleString()}</div>
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Unique</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{jobs.reduce((sum, j) => sum + (j.uniqueRows || j.totalRows || 0), 0).toLocaleString()}</div>
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Duplicates</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{jobs.reduce((sum, j) => sum + (j.duplicateRows || 0), 0).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input 
                type="checkbox" 
                checked={selectedJobs.length === jobs.length && jobs.length > 0}
                onChange={(e) => {
                  if (e.target.checked) setSelectedJobs(jobs.map(j => j.id));
                  else setSelectedJobs([]);
                }}
              />
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Select All</span>
            </div>
            {selectedJobs.length > 0 && (
              <>
                <div style={{ width: '1px', height: '24px', background: '#cbd5e1' }}></div>
                <span style={{ fontSize: '0.9rem', color: '#64748b' }}>{selectedJobs.length} selected</span>
                <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                  <button className="button button--secondary" onClick={() => setShowGlobalDupConfirm(true)}>Remove Duplicates</button>
                  <button className="button button--secondary" style={{ color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => setShowGlobalPrefixConfirm(true)}>Delete by Series</button>
                  <button className="button button--primary" onClick={handleCombineFiles}>Combine Files</button>
                </div>
              </>
            )}
          </div>

          {activeOperation && (
            <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 600, color: '#166534' }}>Operation in Progress: {activeOperation.type}</span>
                <span style={{ color: '#166534' }}>{activeOperation.progress}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', background: '#dcfce3', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#22c55e', width: `${activeOperation.progress}%`, transition: 'width 0.3s' }}></div>
              </div>
            </div>
          )}

          {showGlobalDupConfirm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Global Deduplication</h3>
                <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '16px' }}>Remove duplicates across <strong>{selectedJobs.length}</strong> selected files.</p>
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Columns to check for duplicates:</label>
                  <input type="text" className="form-input" placeholder="e.g. Mobile, Email" value={globalDupCols.join(', ')} onChange={e => setGlobalDupCols(e.target.value.split(',').map(s=>s.trim()).filter(s=>s))} />
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button className="button button--secondary" onClick={() => setShowGlobalDupConfirm(false)}>Cancel</button>
                  <button className="button button--primary" style={{ background: '#ef4444', borderColor: '#ef4444' }} onClick={handleGlobalDeduplicate}>Start Processing</button>
                </div>
              </div>
            </div>
          )}

          {showGlobalPrefixConfirm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Global Delete By Series</h3>
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Column:</label>
                  <input type="text" className="form-input" placeholder="e.g. Mobile" value={globalPrefixCol} onChange={e => setGlobalPrefixCol(e.target.value)} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Prefixes (comma separated):</label>
                  <input type="text" className="form-input" placeholder="e.g. 987, 123" value={globalPrefixString} onChange={e => setGlobalPrefixString(e.target.value)} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Scope:</label>
                  <select className="form-input" value={globalPrefixScope} onChange={e => setGlobalPrefixScope(e.target.value)}>
                    <option value="selected">Selected Files ({selectedJobs.length})</option>
                    <option value="all">All Files ({jobs.length})</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button className="button button--secondary" onClick={() => setShowGlobalPrefixConfirm(false)}>Cancel</button>
                  <button className="button button--primary" style={{ background: '#ef4444', borderColor: '#ef4444' }} onClick={handleGlobalDeletePrefix}>Start Deletion</button>
                </div>
              </div>
            </div>
          )}

          <div className="reports-grid">
            {jobs.map(job => (
              <article key={job.id} className={`report-card ${selectedJobs.includes(job.id) ? 'selected' : ''}`} style={selectedJobs.includes(job.id) ? { borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' } : {}}>
              <div className="report-card__header">
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedJobs.includes(job.id)}
                    onChange={() => toggleJobSelection(job.id)}
                    style={{ marginTop: '4px' }}
                  />
                  <div>
                    <h3>{job.originalName}</h3>
                    <p className="report-id">{job.id}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="button button--secondary" onClick={() => handleDelete(job.id)}>Delete</button>
                  <button className="button button--primary" disabled={!job.columns || job.columns.length === 0} onClick={() => handleViewData(job)}>
                    View Data
                  </button>
                </div>
              </div>

              <div style={{ margin: '16px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', color: job.status === 'error' ? '#ef4444' : '#0ea5e9' }}>
                    Status: {job.status}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {job.progress}%
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: job.status === 'error' ? '#ef4444' : '#0ea5e9', width: `${job.progress}%`, transition: 'width 0.3s' }}></div>
                </div>
              </div>

              <div className="report-card__meta" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '8px', flex: 1, minWidth: '120px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Total Records</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{job.totalRows.toLocaleString()}</div>
                </div>
                <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '8px', flex: 1, minWidth: '120px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Unique</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#334155' }}>{(job.uniqueRows || job.totalRows || 0).toLocaleString()}</div>
                </div>
                <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '8px', flex: 1, minWidth: '120px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Duplicates</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{job.duplicateRows.toLocaleString()}</div>
                </div>
              </div>
              
              {job.error && (
                <div style={{ marginTop: '12px', padding: '8px', background: '#fef2f2', color: '#ef4444', borderRadius: '6px', fontSize: '0.85rem' }}>
                  Error: {job.error}
                </div>
              )}
            </article>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
